// @ts-strict-ignore
import { displayNameForIntegrationType } from './../../../helpers/integrationHelpers';
import { setSetupStepLocalData } from './../../../helpers/localStorageHelper';
import { FreeformFeedbackStep } from './../steps/FreeformFeedback';
import { ReleaseTagsStep } from './../steps/ReleaseTagsStep';
import { FeatureAffiliationStep } from './../steps/FeatureAffiliations';
import { AppStoreStep } from './../steps/AppStore';
import { KeyIntegrationsStep } from './../steps/KeyIntegrations';
import { BranchingStrategyStep } from './../steps/BranchingStrategyStep';
import { useHistory, useLocation, useParams, useRouteMatch } from 'react-router-dom';
import { DefaultReleaseTypeStep } from './../steps/DefaultReleaseTypeStep';
import { AutomationsStep } from '../steps/AutomationsStep';
import {
    AppRouteComponentMatchParams,
    SetupRouteComponentMatchParams,
    SetupStep,
    SetupStepsCallerContext,
    SetupStepType,
} from './../../../typescriptHelpers';
import React, { useEffect } from 'react';
import { createSetupStepRoute } from '../../../helpers/routeHelpers';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../reducers/reducers';
import {
    selectAppPlatform,
    selectDestinationInfoByDestinationStepConfigId,
    selectIntegrationFromIntegrationType,
    selectIsWorkflowsMigratedAndEnabled,
} from '../../../selectors/selectors';
import { getIdFromAppId, getIdFromOrgId, isAppPlatform } from '../../../helpers';
import { AppPlatform, IntegrationType } from '../../../types';
import qs from 'query-string';
import { authTypeForIntegrationType, extractIntegrationRequestFromRedirect } from '../../../helpers/integrationHelpers';
import { AuthUpdatable, IntegrationRefreshable } from '../../../actions/updatable';
import {
    CreateIntegrationRequest,
    RefreshIntegrationRequest,
    tryCreateIntegrationRequest,
    tryRefreshIntegrationRequest,
} from '../../../actions/integrations';
import { globalMessage } from '../../../utils/globalMessage';
import { AppNotificationsStep } from '../steps/AppNotificationsStep';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';

export const SETUP_STEP_BY_TYPE: Record<SetupStepType, SetupStep> = {
    [SetupStepType.DefaultReleaseType]: DefaultReleaseTypeStep,
    [SetupStepType.AppStore]: AppStoreStep,
    [SetupStepType.ReleaseTags]: ReleaseTagsStep,
    [SetupStepType.FeatureAffiliations]: FeatureAffiliationStep,
    [SetupStepType.FreeformFeedback]: FreeformFeedbackStep,
    [SetupStepType.KeyIntegrations]: KeyIntegrationsStep,
    [SetupStepType.Automations]: AutomationsStep,
    [SetupStepType.AppNotifications]: AppNotificationsStep,
    [SetupStepType.BranchingStrategy]: BranchingStrategyStep,
};

export const STEPS_SEQUENCE = [
    SetupStepType.DefaultReleaseType,
    SetupStepType.AppStore,
    SetupStepType.BranchingStrategy,
    SetupStepType.ReleaseTags,
    SetupStepType.FeatureAffiliations,
    SetupStepType.Automations,
    SetupStepType.AppNotifications,
    SetupStepType.KeyIntegrations,
    SetupStepType.FreeformFeedback,
];

function stepsForCallerContext(callerContext: SetupStepsCallerContext): SetupStepType[] {
    switch (callerContext) {
        case SetupStepsCallerContext.BuildDistroConnectCICDIntegration:
        case SetupStepsCallerContext.CommonIntegrationCapability:
            return [SetupStepType.KeyIntegrations];
        default:
            return null;
    }
}

function stepOverridesForCallerContext(callerContext: SetupStepsCallerContext): Partial<SetupStep> {
    switch (callerContext) {
        case SetupStepsCallerContext.BuildDistroConnectCICDIntegration:
            return {
                title: 'Connect your CI/CD provider.',
                description: () => {
                    return 'Link buckets to specific CI/CD workflows to automatically pull in build artifacts for distribution.';
                },
            };
        default:
            return {};
    }
}

interface SetupStateHookData {
    currentSetupStep: SetupStep;
    hasPreviousStep: boolean;
    hasNextStep: boolean;
    goToNextStep: () => void;
    goToPreviousStep: () => void;
}

export const useSetupStepsSequence: (appId: string, callerContext?: SetupStepsCallerContext) => SetupStepType[] = (
    appId,
    callerContext,
) => {
    const isMultiDestinationApp = useSelector((state: RootState) => {
        const isWorkflowsMigratedAndEnabled = selectIsWorkflowsMigratedAndEnabled(state, appId);
        if (!isWorkflowsMigratedAndEnabled) {
            return false;
        }

        return Object.keys(selectDestinationInfoByDestinationStepConfigId(state, appId)).length > 1;
    });

    const platform = useSelector((state: RootState) => selectAppPlatform(state, appId));
    const shouldHideAppStoreStep = isMultiDestinationApp || !isAppPlatform(platform);

    const customStepSequence = stepsForCallerContext(callerContext);
    return (
        customStepSequence ??
        (shouldHideAppStoreStep ? STEPS_SEQUENCE.filter((step) => step !== SetupStepType.AppStore) : STEPS_SEQUENCE)
    );
};

export const useSetupStepsState: (appId: string, callerContext?: SetupStepsCallerContext) => SetupStateHookData = (
    appId,
    callerContext,
) => {
    const customStepSequence = stepsForCallerContext(callerContext);
    const stepsSequence = useSetupStepsSequence(appId, callerContext);

    const match = useRouteMatch<SetupRouteComponentMatchParams>();
    const { setupTypeParam } = match.params;
    const setupStepType = stepsSequence.find((step) => step === setupTypeParam) as SetupStepType;
    const history = useHistory();

    useEffect(() => {
        // Save hasBeenVisited to local data
        setSetupStepLocalData(setupStepType, appId, { hasBeenVisited: true });

        if (!setupStepType) {
            const { appIdParam, orgIdParam } = match.params;

            const firstStep = customStepSequence?.[0] ?? SetupStepType.DefaultReleaseType;
            const firstStepUrl = createSetupStepRoute(orgIdParam, appIdParam, firstStep);
            history.replace({
                pathname: firstStepUrl,
                search: location.search,
            });
        }
    }, [setupStepType]);

    let currentSetupStep = SETUP_STEP_BY_TYPE[setupStepType ?? SetupStepType.DefaultReleaseType];

    currentSetupStep = {
        ...currentSetupStep,
        ...stepOverridesForCallerContext(callerContext),
    };

    const currentStepIndex = stepsSequence.indexOf(setupStepType);
    const hasPreviousStep = currentStepIndex > 0 && stepsSequence.length > 1;
    const hasNextStep = currentStepIndex < stepsSequence.length - 1 && stepsSequence.length > 1;

    const goToNextStep = () => {
        const nextStepType = stepsSequence[currentStepIndex + 1];

        const { appIdParam, orgIdParam } = match.params;
        const nextURL = createSetupStepRoute(orgIdParam, appIdParam, nextStepType);
        history.push({
            pathname: nextURL,
            search: location.search,
        });
    };

    const goToPreviousStep = () => {
        const previousStepType = stepsSequence[currentStepIndex - 1];
        const { appIdParam, orgIdParam } = match.params;
        const previousURL = createSetupStepRoute(orgIdParam, appIdParam, previousStepType);
        history.push({
            pathname: previousURL,
            search: location.search,
        });
    };

    return {
        currentSetupStep,
        hasPreviousStep,
        hasNextStep,
        goToNextStep,
        goToPreviousStep,
    };
};

export interface OauthIntegrationInstallerHookData {
    platform?: AppPlatform;
}

export const useOauthIntegrationInstaller: (data: OauthIntegrationInstallerHookData) => void = ({ platform }) => {
    const params = useParams<AppRouteComponentMatchParams>();
    const { appIdParam, orgIdParam } = params;
    const history = useHistory();

    const [shouldStartInstall, setShouldStartInstall] = React.useState<boolean>(false);
    const [integrationError, setIntegrationError] = React.useState<string>(null);

    const orgId = useSelector((state: RootState) => {
        if (orgIdParam) {
            return getIdFromOrgId(orgIdParam, state);
        }
        return null;
    });

    const appId = useSelector((state: RootState) => {
        if (orgIdParam && appIdParam) {
            return getIdFromAppId(orgIdParam, appIdParam, state);
        }
        return null;
    });

    const location = useLocation();
    const queryParams = qs.parse(location.search);

    const parsedIntegrationType = Object.values(IntegrationType).find(
        (type) => type === queryParams.installIntegrationId,
    ) as IntegrationType;

    const selectedIntegration = useSelector(
        (state: RootState) => appId && selectIntegrationFromIntegrationType(state, appId, parsedIntegrationType),
    );

    const authType = authTypeForIntegrationType(parsedIntegrationType);

    const addIntegrationIsLoading = useIsLoading(authType);
    const addIntegrationError = useError(authType);
    const addIntegrationSuccess = useUpdated(authType, selectedIntegration?.installationId ?? null);

    React.useEffect(() => {
        if (addIntegrationIsLoading) {
            globalMessage.loading({
                content: `Installing ${displayNameForIntegrationType(parsedIntegrationType)} integration...`,
                key: 'install-integration',
                duration: 0,
            });
        } else {
            globalMessage.destroy('install-integration');
        }
    }, [addIntegrationIsLoading]);

    React.useEffect(() => {
        if (addIntegrationSuccess) {
            globalMessage.success(`${displayNameForIntegrationType(parsedIntegrationType)} integration installed!`);

            // clean url after installation
            history.replace({ search: '', state: { lastInstalledIntegrationType: parsedIntegrationType } });
        }
    }, [addIntegrationSuccess, location]);

    React.useEffect(() => {
        if (addIntegrationError || integrationError) {
            globalMessage.error(addIntegrationError || integrationError);
        }
    }, [addIntegrationError, integrationError]);

    const dispatch = useDispatch();
    const addIntegration = (type: AuthUpdatable, request: CreateIntegrationRequest) => {
        dispatch(
            tryCreateIntegrationRequest(type, {
                ...request,
                data: {
                    ...request.data,
                    platform,
                },
            }),
        );
    };

    const refreshIntegration = (type: IntegrationRefreshable, request: RefreshIntegrationRequest) => {
        dispatch(
            tryRefreshIntegrationRequest(type, {
                ...request,
            }),
        );
    };

    const integrationCreateOrRefreshRequestOrErrorData =
        parsedIntegrationType &&
        queryParams &&
        orgId &&
        orgIdParam &&
        extractIntegrationRequestFromRedirect(parsedIntegrationType, queryParams, orgId, appId, orgIdParam, appIdParam);

    React.useEffect(() => {
        if (integrationCreateOrRefreshRequestOrErrorData) {
            if (integrationCreateOrRefreshRequestOrErrorData.createRequest) {
                setShouldStartInstall(true);
            } else if (integrationCreateOrRefreshRequestOrErrorData.error) {
                setIntegrationError(integrationCreateOrRefreshRequestOrErrorData.error);
            }
        }
    }, [integrationCreateOrRefreshRequestOrErrorData]);

    React.useEffect(() => {
        if (shouldStartInstall) {
            if (integrationCreateOrRefreshRequestOrErrorData.createRequest) {
                addIntegration(
                    integrationCreateOrRefreshRequestOrErrorData.authType,
                    integrationCreateOrRefreshRequestOrErrorData.createRequest,
                );
            } else if (
                integrationCreateOrRefreshRequestOrErrorData.refreshRequest &&
                integrationCreateOrRefreshRequestOrErrorData.integrationRefreshable
            ) {
                refreshIntegration(
                    integrationCreateOrRefreshRequestOrErrorData.integrationRefreshable,
                    integrationCreateOrRefreshRequestOrErrorData.refreshRequest,
                );
            }
        }
    }, [shouldStartInstall]);
};
